/**
 * @file TasksFilterBar.tsx
 * @description Строка отборов страницы «Задачи» (phase19.31, SC60; crm.md
 * T7): пилюли «Мои / Все / Просрочено / Без исполнителя» со счётчиками,
 * пилюли видов «Задачи / Звонки / Встречи» и флажок «Показать выполненные».
 *
 * На телефоне видны только пилюли отбора — виды и «выполненные» уходят в
 * шторку «Фильтры · N», чтобы под фильтрами оставалось место для списка.
 */

import { Checkbox } from "../../ui/checkbox";
import { FilterChips, FilterToggleChips } from "../../ui/FilterChips";
import { FilterSheetButton } from "../../ui/FilterSheetButton";
import { PHONE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import type { TaskCountsDto } from "../../../lib/api-crm";
import {
  TASK_KINDS,
  TASK_KIND_FILTER_LABEL,
  TASK_SCOPE_CHIPS,
  resetTasksFilters,
  tasksFilterCount,
  toggleTaskKind,
  type TaskScope,
  type TasksQuery,
} from "./tasks-query";

interface Props {
  query: TasksQuery;
  counts: TaskCountsDto | null;
  onChange: (patch: Partial<TasksQuery>) => void;
}

export function TasksFilterBar({ query, counts, onChange }: Props) {
  const phone = useMediaQuery(PHONE_QUERY);

  const secondary = (
    <>
      <FilterToggleChips
        aria-label="Вид дела"
        items={TASK_KINDS.map((kind) => ({
          key: kind,
          label: TASK_KIND_FILTER_LABEL[kind],
          pressed: query.kinds.includes(kind),
          onToggle: () => onChange({ kinds: toggleTaskKind(query.kinds, kind) }),
        }))}
      />
      <label className="flex min-h-12 cursor-pointer items-center gap-2 px-1 type-body text-on-surface">
        <Checkbox checked={query.showDone} onCheckedChange={(checked) => onChange({ showDone: checked === true })} />
        Показать выполненные
      </label>
    </>
  );

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-6">
      {/* max-w-full: ряд не шире страницы — пилюли переносятся (QA-5 D1). */}
      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
        <FilterChips<TaskScope>
          aria-label="Отбор задач"
          value={query.scope}
          onChange={(scope) => onChange({ scope })}
          items={TASK_SCOPE_CHIPS.map((chip) => ({
            value: chip.value,
            label: chip.label,
            count: counts ? counts[chip.countKey] : undefined,
          }))}
        />
        {phone ? (
          <FilterSheetButton
            count={tasksFilterCount(query)}
            onReset={() => onChange(resetTasksFilters(query))}
          >
            {secondary}
          </FilterSheetButton>
        ) : null}
      </div>
      {phone ? null : <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{secondary}</div>}
    </div>
  );
}
